/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Boundary } from '@floating-ui/react';
import classNames from 'classnames';
import type { AugmentedTextTrack } from 'video.js';

/* eslint-disable no-restricted-imports */
import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import type { QuizQuestionPrompt } from 'bundles/compound-assessments/types/FormParts';
import type { VideoPlayer as VideoPlayerType } from 'bundles/item-lecture';
import type { VideoFeedbackState } from 'bundles/video-player/components/ActionFeedback';
import ActionFeedback from 'bundles/video-player/components/ActionFeedback';
import ControlBar from 'bundles/video-player/components/ControlBar';
import AutoNextOverlay from 'bundles/video-player/components/autoplay/AutoNextOverlay';
import PlayButton from 'bundles/video-player/components/video/PlayButton';
import { VIDEOPLAYER_FOCUS_OUTLINE_THICKNESS } from 'bundles/video-player/constants';
import type { DubbedSources, DubbedSubtitlesVtt, SourcesByResolution } from 'bundles/video-player/types/VideoContent';
import type { VideoPlayerEventingV3UIFunction, VideoPopup } from 'bundles/video-player/types/VideoPlayer';
import { applySubtitlePosition } from 'bundles/video-player/utils/trackUtils';
import userPreferences from 'bundles/video-player/utils/userPreferences';
import type { InVideoQuestion } from 'bundles/video-quiz/index';

const styles = {
  videoControlsContainer: css`
    position: absolute;
    bottom: 0;
    z-index: 2;

    /* If changing the full-width and full-height nature of this container, make sure to update the code in 
     * the handlePlayerDblClick function in static/bundles/video-player/components/video/VideoPlayer.tsx to 
     * ensure double clicks are triggered when clicking on the right element
     */
    width: 100%;
    height: 100%;
    background-color: rgba(0 0 0 0%);
    display: flex;
    justify-content: center;
    align-items: center;

    &.showVideoBanner {
      z-index: 10000;
    }

    &.hideContextMenu {
      pointer-events: none;

      & > * {
        pointer-events: auto;
      }
    }

    button:focus,
    button:active {
      outline: ${VIDEOPLAYER_FOCUS_OUTLINE_THICKNESS}px solid #82a8e1;
    }

    .shareButton {
      position: absolute;
      right: 10px;
      top: 18px;
      transition: opacity 200ms ease-out;
    }
  `,
};

const HIDE_CONTROLS_AFTER_TIMEOUT_MS = 5000;
const SPACE_BAR_KEY = 32;
const ENTER_KEY = 13;
const VIDEO_CONTROLS_CONTAINER_CLASSNAME = 'rc-VideoControlsContainer';
export const VIDEO_CONTROLS_CONTAINER_SELECTOR = `.${VIDEO_CONTROLS_CONTAINER_CLASSNAME}`;

type Props = {
  defaultSubtitleLanguage: string;
  nextItem: ItemMetadata;
  itemMetadata: ItemMetadata;
  player: VideoPlayerType;
  videoQuizQuestions: InVideoQuestion<QuizQuestionPrompt>[] | null;
  visibility?: boolean;
  onChangeFullScreen: (isFullscreen: boolean) => void;
  isFullscreen: boolean;
  autoNextCountdownSeconds?: number;
  isAutoNextPaused?: boolean;
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction;
  trackVideoPlayerViewEvent: VideoPlayerEventingV3UIFunction;
  trimmedStartTimeSeconds: number;
  trimmedDurationSeconds: number | null;
  enableHls?: boolean;
  videoBoundaryElement?: Boundary;
  dubbedSources: DubbedSources;
  originalSources: SourcesByResolution;
  dubbedSubtitlesVtt: DubbedSubtitlesVtt;
  videoFeedbackState?: VideoFeedbackState;
  showFeedback: boolean;
  showInitialCenterPlayButton: boolean;
  displayInitialCenterPlayButton: (showInitialCenterPlayButton: boolean) => void;
  hlsSource?: string;
};

type State = {
  hideContextMenu: boolean;
  isChromeVisible: boolean;
  showAutoNextOverlay: boolean;
  visiblePopup: VideoPopup;
};

class VideoControlsContainer extends React.Component<Props, State> {
  declare node: HTMLElement | null | undefined;

  declare timeoutID: number | null | undefined;

  state = {
    isChromeVisible: false,
    showAutoNextOverlay: false,
    visiblePopup: null,
    // disables the context menu on the container-level so
    // users can use the <video> element's context menu
    hideContextMenu: false,
  };

  componentDidMount() {
    const { player, displayInitialCenterPlayButton } = this.props;

    player.one('play', () => {
      displayInitialCenterPlayButton(false);
    });

    player.on('play', () => {
      this.setState({ showAutoNextOverlay: false });
      const textTracks = Array.from(player.textTracks()) as AugmentedTextTrack[];
      applySubtitlePosition({ textTracks });
      this.showChromeOnControlUse();
    });

    player.on('seeked', this.showChromeOnControlUse);
    player.on('volumechange', this.showChromeOnControlUse);
    player.on('pause', this.showChromeOnControlUse);

    player.on('ended', () => {
      // player.autoplay() refers to autoplaying the video when the page is loaded.
      // This assumes the "autoplay experience" includes the above, as well as displaying
      // the auto next overlay when a video ends
      // see: https://blog.videojs.com/autoplay-best-practices-with-video-js/
      // TODO: move this and other settings to backend API.
      this.setState({ showAutoNextOverlay: true });
    });

    displayInitialCenterPlayButton(!player.autoplay());

    document.addEventListener('mousedown', this.handleDocumentMouseDown);
    document.addEventListener('mousemove', this.handleDocumentMouseMove);
  }

  componentWillUnmount() {
    const { player } = this.props;

    player.off('ended');

    this.clearChromeVisibilityTimeout();

    document.removeEventListener('mousedown', this.handleDocumentMouseDown);
    document.removeEventListener('mousemove', this.handleDocumentMouseMove);
  }

  handleDocumentMouseMove = () => {
    const { hideContextMenu } = this.state;

    if (hideContextMenu) {
      this.setState({
        hideContextMenu: false,
      });
    }
  };

  handleVideoRegionMouseMove = () => {
    this.showChromeOnControlUse();
  };

  handleDocumentMouseDown = (event: MouseEvent) => {
    const { player } = this.props;
    const isFullscreen = player.isFullscreen();

    if (this.node && !this.node.contains(event.target as HTMLElement) && !isFullscreen) {
      if (!player.paused()) {
        this.setChromeVisibility(false);
      }
    }
  };

  onPlayButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { player, displayInitialCenterPlayButton, trackVideoPlayerClickEvent } = this.props;
    player.play();
    displayInitialCenterPlayButton(false);
    trackVideoPlayerClickEvent('video_center_play', {});
  };

  onPlayButtonKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.keyCode === SPACE_BAR_KEY || event.keyCode === ENTER_KEY) {
      const { player, displayInitialCenterPlayButton } = this.props;
      player.play();
      displayInitialCenterPlayButton(false);
    }
  };

  onClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { player } = this.props;

    if (player.paused()) {
      player.play();
    } else {
      player.pause();
    }
  };

  onShareClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    const { player } = this.props;

    if (!player.paused()) {
      player.pause();
    }
  };

  onContextMenu = () => {
    this.setState({
      hideContextMenu: true,
    });
  };

  hideChrome = (event: React.FocusEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    const { visiblePopup } = this.state;
    const { player } = this.props;
    const isFullscreen = player.isFullscreen();

    if (!player.paused() && !isFullscreen && visiblePopup === null) {
      this.setChromeVisibility(false);
    }
  };

  hideChromeOnBlur = (event: React.FocusEvent<HTMLElement>) => {
    // For a11y, do not trigger when swapping focus between children. Trigger only when focus leaves VideoControlsContainer.
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      this.hideChrome(event);
    }
  };

  showChrome = (event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>) => {
    event.stopPropagation();

    this.setChromeVisibility(true);
  };

  showChromeOnFocus = (event: React.FocusEvent<HTMLElement>) => {
    this.showChrome(event);

    //  Clear the default timeout that hides the video control bar after 5 seconds. For better a11y, continue to show the control bar for users who are navigating it via the tab key.
    this.clearChromeVisibilityTimeout();
  };

  startChromeVisibilityTimeout = () => {
    this.timeoutID = window.setTimeout(() => {
      this.setChromeVisibility(false);
    }, HIDE_CONTROLS_AFTER_TIMEOUT_MS);
  };

  clearChromeVisibilityTimeout = () => {
    if (this.timeoutID) {
      clearTimeout(this.timeoutID);
    }
  };

  showChromeOnControlUse = () => {
    this.clearChromeVisibilityTimeout();
    const { player } = this.props;
    this.setChromeVisibility(true);

    const controlBarHasFocus = this.node?.contains(document.activeElement);
    if (!player.paused() && !controlBarHasFocus) {
      this.startChromeVisibilityTimeout();
    }
  };

  setChromeVisibility = (visible: boolean) => {
    const { visiblePopup } = this.state;

    this.setState({
      isChromeVisible: visible,
      visiblePopup: visible ? visiblePopup : null,
    });
  };

  setVisiblePopup = (popupName: VideoPopup) => {
    this.setState({
      visiblePopup: popupName,
    });
  };

  assignRef = (node: HTMLElement | null) => {
    this.node = node;
  };

  render() {
    const {
      defaultSubtitleLanguage,
      nextItem,
      player,
      videoQuizQuestions,
      visibility,
      onChangeFullScreen,
      autoNextCountdownSeconds,
      itemMetadata,
      isAutoNextPaused,
      trackVideoPlayerClickEvent,
      trackVideoPlayerViewEvent,
      trimmedStartTimeSeconds,
      trimmedDurationSeconds,
      enableHls,
      videoBoundaryElement,
      videoFeedbackState,
      showFeedback,
      showInitialCenterPlayButton,
      dubbedSources,
      originalSources,
      dubbedSubtitlesVtt,
      hlsSource,
    } = this.props;

    const { isChromeVisible, visiblePopup, showAutoNextOverlay, hideContextMenu } = this.state;

    const isAutoNextEnabled = isAutoNextPaused ? false : userPreferences && userPreferences.get('autoplay');
    // When a video is trimmed, the _techget ended flag doesnt get set (because this layer's duration/currentTime dont seem to get updated with the player itself)
    const shouldShowAutoNext =
      showAutoNextOverlay && visibility && nextItem && (player.ended() || trimmedDurationSeconds);
    return (
      <div
        ref={this.assignRef}
        role="presentation"
        className={classNames(VIDEO_CONTROLS_CONTAINER_CLASSNAME, { hideContextMenu })}
        onClick={this.onClick}
        onFocus={this.showChromeOnFocus}
        onContextMenu={this.onContextMenu}
        onBlur={this.hideChromeOnBlur}
        onMouseEnter={!showInitialCenterPlayButton ? this.showChrome : undefined}
        onMouseLeave={this.hideChrome}
        onMouseMove={this.handleVideoRegionMouseMove}
        css={styles.videoControlsContainer}
      >
        {showInitialCenterPlayButton && (
          <PlayButton
            onClick={this.onPlayButtonClick}
            onKeyPress={this.onPlayButtonKeyPress}
            haspopup={false}
            videoName={itemMetadata?.attributes?.name}
          />
        )}
        <ActionFeedback feedback={videoFeedbackState} showFeedback={showFeedback} />
        {shouldShowAutoNext && (
          <AutoNextOverlay
            nextItem={nextItem}
            isAutoNextEnabled={isAutoNextEnabled}
            defaultCountdown={autoNextCountdownSeconds}
            isAutoNextPaused={isAutoNextPaused}
          />
        )}
        <ControlBar
          courseId={itemMetadata?.attributes?.course?.id || ''}
          enableHls={enableHls}
          hlsSource={hlsSource}
          visiblePopup={visiblePopup}
          videoQuizQuestions={videoQuizQuestions}
          setVisiblePopup={this.setVisiblePopup}
          visible={isChromeVisible}
          player={player}
          onChangeFullScreen={onChangeFullScreen}
          trackVideoPlayerClickEvent={trackVideoPlayerClickEvent}
          trackVideoPlayerViewEvent={trackVideoPlayerViewEvent}
          trimmedStartTimeSeconds={trimmedStartTimeSeconds}
          videoBoundaryElement={videoBoundaryElement}
          defaultSubtitleLanguage={defaultSubtitleLanguage}
          dubbedSubtitlesVtt={dubbedSubtitlesVtt}
          dubbedSources={dubbedSources}
          originalSources={originalSources}
        />
      </div>
    );
  }
}

export default VideoControlsContainer;
